import Feature from 'ol/Feature'
import Point from 'ol/geom/Point'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import CircleStyle from 'ol/style/Circle'
import Fill from 'ol/style/Fill'
import Stroke from 'ol/style/Stroke'
import Style from 'ol/style/Style'
import Text from 'ol/style/Text'
import { darkBlue } from 'utils/constants/ColorTheme'
import { getSetting } from 'utils/SettingUtils'

// Paramètres applicatifs (administration > paramètres > cartographie)
const CLUSTER_SETTINGS = {
    ENABLED: 'cartoClusterEnabled',
    MIN_SIZE: 'cartoClusterMinSize',
    DISTANCE: 'cartoClusterDistance',
}
// Nombre de stations proches à partir duquel elles sont remplacées par une bulle
const CLUSTER_MIN_SIZE = 2
// Distance à l'écran (en pixels) en dessous de laquelle des stations sont regroupées
const CLUSTER_DISTANCE_PX = 40

const getClusterOptions = (applicationSettings = []) => {
    const minSize = parseInt(getSetting(applicationSettings, CLUSTER_SETTINGS.MIN_SIZE))
    const distancePx = parseInt(getSetting(applicationSettings, CLUSTER_SETTINGS.DISTANCE))
    return {
        enabled: getSetting(applicationSettings, CLUSTER_SETTINGS.ENABLED) === '1',
        minSize: isNaN(minSize) ? CLUSTER_MIN_SIZE : Math.max(CLUSTER_MIN_SIZE, minSize),
        distancePx: isNaN(distancePx) ? CLUSTER_DISTANCE_PX : Math.max(0, distancePx),
    }
}
const CLUSTER_LAYER_Z_INDEX = 9000
const BUBBLE_RADIUS = 16
const HIGHLIGHTED_BUBBLE_RADIUS = 24

const SAVED_STYLE = 'clusterSavedStyle'
const HIDDEN_FEATURES = 'clusterHiddenFeatures'
const CLUSTER_FEATURES = 'clusterFeatures'
const EMPTY_STYLE = new Style({})

// Style réel d'une station, même quand elle est masquée dans une bulle
const getFeatureStyle = feature => feature.get(SAVED_STYLE) || feature.getStyle()

const getClusterFeatures = feature => feature.get(CLUSTER_FEATURES)

const clusterLayers = new Set()

const createClusterLayer = () => {
    const clusterLayer = new VectorLayer({
        source: new VectorSource(),
        zIndex: CLUSTER_LAYER_Z_INDEX,
    })
    clusterLayers.add(clusterLayer)
    return clusterLayer
}

const removeClusterLayer = clusterLayer => {
    clusterLayers.delete(clusterLayer)
}

const getBubbleStyle = (count, highlighted = false) => {
    const radius = highlighted ? HIGHLIGHTED_BUBBLE_RADIUS : BUBBLE_RADIUS
    return new Style({
        image: new CircleStyle({
            radius,
            fill: new Fill({ color: darkBlue }),
            stroke: new Stroke({ color: 'white', width: 2 }),
            displacement: [0, radius],
        }),
        text: new Text({
            text: `${count}`,
            font: `bold ${highlighted ? 18 : 13}px Arial`,
            fill: new Fill({ color: 'white' }),
            offsetY: -radius,
        }),
        zIndex: highlighted ? 1 : 0,
    })
}

// Agrandit la bulle qui contient la station, tant qu'elle est survolée ailleurs (tableau)
const highlightClusterOfFeature = (feature, highlighted) => {
    clusterLayers.forEach(clusterLayer => {
        clusterLayer.getSource().getFeatures().forEach(bubble => {
            const stations = getClusterFeatures(bubble)
            bubble.setStyle(getBubbleStyle(stations.length, highlighted && stations.includes(feature)))
        })
    })
}

const isStationFeature = feature => feature.get('hover') === true && feature.getGeometry()?.getType() === 'Point'

const getVisibleStationFeatures = layers => layers.flatMap(layer => {
    const olLayer = layer?.getLayer?.()
    const source = olLayer?.getVisible() && olLayer.getSource?.()
    return source?.getFeatures ? source.getFeatures().filter(isStationFeature) : []
})

const showFeature = feature => {
    feature.setStyle(feature.get(SAVED_STYLE))
    feature.unset(SAVED_STYLE, true)
}

const hideFeature = feature => {
    feature.set(SAVED_STYLE, feature.getStyle(), true)
    feature.setStyle(EMPTY_STYLE)
}

// Regroupe les stations distantes de moins de `distance` (unités carte) de la première station du groupe.
// Grille de cellules de la taille de la distance : seules les 9 cellules voisines sont comparées.
const groupByProximity = (features, distance) => {
    const groups = []
    const grid = {}
    features.forEach(feature => {
        const [x, y] = feature.getGeometry().getCoordinates()
        const cellX = Math.floor(x / distance)
        const cellY = Math.floor(y / distance)
        const neighbours = [-1, 0, 1].flatMap(dx => [-1, 0, 1].flatMap(dy => grid[`${cellX + dx}|${cellY + dy}`] ?? []))
        const group = neighbours.find(g => Math.hypot(g.x - x, g.y - y) <= distance)
        if (group) {
            group.features.push(feature)
        } else {
            const newGroup = { x, y, features: [feature] }
            groups.push(newGroup)
            const key = `${cellX}|${cellY}`
            grid[key] = [...(grid[key] ?? []), newGroup]
        }
    })
    return groups.map(g => g.features)
}

const getCenter = group => {
    const coordinates = group.map(feature => feature.getGeometry().getCoordinates())
    return [0, 1].map(axis => coordinates.reduce((sum, c) => sum + c[axis], 0) / coordinates.length)
}

const refreshClusters = (clusterLayer, layers = [], resolution = 0, options = getClusterOptions()) => {
    // Regroupement par proximité désactivé : seules les stations exactement à la même position sont regroupées
    const distance = options.enabled ? Math.max(options.distancePx * resolution, Number.EPSILON) : Number.EPSILON
    const minSize = options.enabled ? options.minSize : CLUSTER_MIN_SIZE
    const clusters = groupByProximity(getVisibleStationFeatures(layers), distance)
        .filter(group => group.length >= minSize)

    // Seules les stations qui entrent ou sortent d'une bulle changent de style (chaque changement redessine la couche)
    const previouslyHidden = new Set(clusterLayer.get(HIDDEN_FEATURES) ?? [])
    const hiddenFeatures = new Set(clusters.flat())
    previouslyHidden.forEach(feature => {
        if (!hiddenFeatures.has(feature)) {
            showFeature(feature)
        }
    })
    hiddenFeatures.forEach(feature => {
        if (!previouslyHidden.has(feature)) {
            hideFeature(feature)
        }
    })
    clusterLayer.set(HIDDEN_FEATURES, [...hiddenFeatures], true)

    const bubbles = clusters.map(group => {
        const bubble = new Feature({ geometry: new Point(getCenter(group)) })
        bubble.set(CLUSTER_FEATURES, group, true)
        bubble.setStyle(getBubbleStyle(group.length))
        return bubble
    })
    const source = clusterLayer.getSource()
    source.clear()
    source.addFeatures(bubbles)
}

export { CLUSTER_SETTINGS, getClusterOptions, createClusterLayer, removeClusterLayer, refreshClusters, getFeatureStyle, getClusterFeatures, highlightClusterOfFeature }
